import qrcode from "qrcode-generator";
import { isOwnMediaUrl } from "@gooner/shared";
import { isSafeMode } from "../../lib/safe-mode";
import { drawCrown } from "./Crown";

/**
 * The shared vocabulary for our canvas share cards.
 *
 * WHY THIS FILE EXISTS. There were two card renderers — the profile's `renderStatsCard`
 * and the session summary's `shareImage` — and each carried its own private copy of the
 * palette, the rounded rect and the brand lockup. That is exactly how the session card
 * ended up painting its title `#ff2d78`, a hot pink that appears nowhere else in the
 * client: a brand pass updated every DOM lockup on the site and had no way to reach a
 * second slab of canvas code that nothing imported. A colour that lives in one place
 * cannot drift; a colour copied into two files already has.
 *
 * These are the PRIMITIVES only. Layout stays with each card, because the two cards say
 * different things and a shared layout would be a template with a dozen flags.
 */

/** The public host shown on + encoded into every share card. Change in one place. */
export const SITE_HOST = "goonlords.com";

/**
 * The palette, mirroring `client/src/index.css`'s `@theme` block — a canvas cannot read a
 * CSS custom property, so these are the one legitimate copy. Keep them in step by name.
 */
export const INK = "#0a0a0b"; // --color-ink
export const PANEL = "#141417"; // --color-panel
export const GOLD = "#f5c518"; // --color-accent
/** --color-accent-soft. The profile card used to spell this `#f7d971`, a fourth gold that
 *  existed in that one file; it is the token's value now, everywhere. */
export const GOLD_SOFT = "#ffd95e";
export const TEXT = "#fafafa";
export const MUTED = "#a1a1aa";
export const LABEL = "#71717a";
export const TILE_FILL = "#141417";
export const TILE_STROKE = "#232327";
export const TRACK = "#26262b";
export const EMPTY = "#1a1a1e";

/** Gold at an alpha — the ramp the activity heatmap and the taste bar both step through. */
export const goldA = (a: number) => `rgba(245,197,24,${a})`;

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** The card's ground: ink, a gold glow off the top edge, and a hairline gold frame. */
export function drawCardGround(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, w, h);
  const glow = ctx.createRadialGradient(w / 2, -140, 60, w / 2, -140, 760);
  glow.addColorStop(0, goldA(0.22));
  glow.addColorStop(1, goldA(0));
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, Math.min(h, 640));
  ctx.strokeStyle = goldA(0.22);
  ctx.lineWidth = 2;
  roundRect(ctx, 22, 22, w - 44, h - 44, 30);
  ctx.stroke();
}

/**
 * The real brand artwork, in its two lengths.
 *
 * `NAME` is the logo — crown over the pair, then "goonlords". `DOTCOM` is that same
 * artwork carrying the TLD, so it reads as a url rather than as a name; despite its
 * filename saying "wordmark" it ends in ".com", which is why nothing appends one.
 *
 * NAME is cropped from DOTCOM at the character boundary before the ".", so the two cannot
 * drift: same crown, same letterforms, one master.
 */
export const BRAND_NAME_URL = "/brand/goonlords-name-512.png";
export const BRAND_DOTCOM_URL = "/brand/goonlords-wordmark-crown.png";

/**
 * Decode brand artwork for a canvas card.
 *
 * SEPARATE FROM `loadPosters`, and the differences are the point:
 *
 * - It is NOT skipped in safe mode. Safe mode hides MEDIA; the logo is not media, and a
 *   card that loses its branding for a viewer with the blur on would be the one place the
 *   switch changed the brand rather than the content.
 * - It does NOT set `crossOrigin`. These are our own static files on the page's own origin,
 *   so nothing taints; setting it would ask for CORS headers a static route has no reason
 *   to send and turn a working load into a failing one — the trap loadPosters documents.
 *
 * A miss is simply absent from the map, and every caller has a text fallback: artwork that
 * did not arrive must not be the difference between a card and no card.
 */
export async function loadBrandImages(
  urls: string[],
): Promise<Map<string, HTMLImageElement>> {
  const out = new Map<string, HTMLImageElement>();
  await Promise.all(
    urls.map(
      (u) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          let done = false;
          const finish = (ok: boolean) => {
            if (done) return;
            done = true;
            if (ok) out.set(u, img);
            resolve();
          };
          const t = setTimeout(() => finish(false), POSTER_TIMEOUT_MS);
          img.onload = () => {
            clearTimeout(t);
            finish(true);
          };
          img.onerror = () => {
            clearTimeout(t);
            finish(false);
          };
          img.src = u;
        }),
    ),
  );
  return out;
}

/** Draw an image at a given height, preserving aspect. Returns the width it took. */
export function drawImageH(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  height: number,
): number {
  const w = (img.naturalWidth / Math.max(1, img.naturalHeight)) * height;
  ctx.drawImage(img, x, y, w, height);
  return w;
}

/**
 * The brand lockup as GEOMETRY: the crown polygon, a gap, then the word, drawn from `x` at
 * text baseline `y`. Returns the total width so a caller can lay out around the pair.
 *
 * The fallback now that the cards load the real artwork — kept because a card whose logo
 * PNG did not arrive should still be branded. Measured as one unit rather than shipped as a
 * glyph: the chess-queen character is a different shape in every font and was never our
 * mark (see Crown.tsx — and note tools/checks/crown-mark.mts greps this file for that very
 * character, so it cannot be quoted here even to name it).
 */
export function drawBrandLockup(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size = 40,
): number {
  const markH = Math.round(size * 0.85);
  const gap = Math.round(size * 0.35);
  ctx.save();
  ctx.textAlign = "left";
  ctx.fillStyle = GOLD;
  ctx.font = `bold ${size}px system-ui, sans-serif`;
  const markW = drawCrown(ctx, x, y - markH + 6, markH, GOLD);
  ctx.fillText("goonlords", x + markW + gap, y);
  const total = markW + gap + ctx.measureText("goonlords").width;
  ctx.restore();
  return total;
}

/**
 * An OUTLINED gold pill with centred text — the earned-tier badge.
 *
 * Deliberately the inverse of Gold.tsx's `drawGoldPill`, which is a SOLID gold ground with
 * an ink crown: one is a rank you goon your way into, the other a tier you pay for, and
 * they can appear on the same card. Fill versus outline is what keeps them apart.
 *
 * The caller passes `w` rather than a padding, because the pill's width is a layout
 * decision — the profile card right-aligns it against its own margin and so has to know
 * the width before this is called. Deriving it here instead moved that card's pill by a
 * pixel, which a diff caught and nobody would have.
 */
export function drawPill(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  w: number,
  h: number,
  fontSize = Math.round(h * 0.56),
) {
  ctx.save();
  ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fillStyle = goldA(0.12);
  ctx.fill();
  ctx.strokeStyle = goldA(0.55);
  ctx.lineWidth = Math.max(2, h * 0.0417);
  ctx.stroke();
  ctx.fillStyle = GOLD;
  ctx.textAlign = "center";
  // Alphabetic, at two thirds of the height — the metric the profile card has always used.
  // A `middle` baseline is the obvious choice and sits ~6px higher at this size.
  ctx.textBaseline = "alphabetic";
  ctx.fillText(text, x + w / 2, y + h * 0.6667);
  ctx.restore();
}

/**
 * Set a bold font that makes `text` fit inside `maxW`, shrinking from `size` but never
 * below `min`. Only the size gives — the weight is what makes a headline read as one.
 */
export function fitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxW: number,
  size: number,
  min = 24,
) {
  let s = size;
  ctx.font = `bold ${s}px system-ui, sans-serif`;
  while (s > min && ctx.measureText(text).width > maxW) {
    s -= 2;
    ctx.font = `bold ${s}px system-ui, sans-serif`;
  }
}

/**
 * A stat tile: value over label, in the panel fill with a hairline stroke.
 *
 * The ratios are not free-hand — they are the profile card's absolute numbers divided by
 * its tile height of 122 (42px value at baseline 62, 21px label at baseline 98), so
 * extracting this function moved that card by exactly zero pixels. Change them and you
 * change a shipped card; add a parameter instead.
 */
export function drawStatTile(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  value: string,
  label: string,
  hot = false,
) {
  roundRect(ctx, x, y, w, h, 18);
  ctx.fillStyle = TILE_FILL;
  ctx.fill();
  ctx.strokeStyle = TILE_STROKE;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = hot ? GOLD : TEXT;
  // The value has to fit rather than run past the stroke — five tiles are narrower than
  // four. Only the size gives; the weight is what makes these read as the headline.
  fitText(ctx, value, w - 40, Math.round(h * 0.344));
  ctx.fillText(value, x + 20, y + h * 0.5082);
  ctx.fillStyle = LABEL;
  ctx.font = `600 ${Math.round(h * 0.1721)}px system-ui, sans-serif`;
  ctx.fillText(label.toUpperCase(), x + 20, y + h * 0.8033);
}

/**
 * Cover-crop an image into a rounded box and lay a top-down scrim over it.
 *
 * Cover rather than fit: posters are portrait more often than not and these slots are
 * landscape, so fitting would letterbox every one of them. The scrim is what keeps a label
 * readable over a bright frame.
 */
export function drawCoverImage(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  radius = 18,
) {
  ctx.save();
  roundRect(ctx, x, y, w, h, radius);
  ctx.clip();
  ctx.fillStyle = TILE_FILL;
  ctx.fillRect(x, y, w, h);
  const iw = img.naturalWidth || 1;
  const ih = img.naturalHeight || 1;
  const scale = Math.max(w / iw, h / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, "rgba(10,10,11,0.75)");
  g.addColorStop(0.55, "rgba(10,10,11,0.05)");
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
  ctx.restore();
}

/** Paint a white-backed QR of `data` at (x,y). */
export function drawQr(
  ctx: CanvasRenderingContext2D,
  data: string,
  x: number,
  y: number,
  size: number,
) {
  const qr = qrcode(0, "M");
  qr.addData(data);
  qr.make();
  const n = qr.getModuleCount();
  const quiet = 14;
  ctx.fillStyle = "#ffffff";
  roundRect(ctx, x, y, size, size, 12);
  ctx.fill();
  const cell = (size - quiet * 2) / n;
  ctx.fillStyle = INK;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (qr.isDark(r, c)) {
        ctx.fillRect(
          x + quiet + c * cell,
          y + quiet + r * cell,
          Math.ceil(cell),
          Math.ceil(cell),
        );
      }
    }
  }
}

const POSTER_TIMEOUT_MS = 4000;

/**
 * Decode the posters a card wants to draw, FAIL-CLOSED. Keyed by whatever `id` the caller
 * supplied; a poster that does not arrive is simply absent from the map.
 *
 * Three ways this goes wrong, and all three end as a placeholder rather than a broken card:
 *
 * - A FOREIGN poster taints the canvas and makes toDataURL throw, which would leave the
 *   modal spinning forever with nothing logged. An evicted or never-cached clip legitimately
 *   arrives with a reddit/redgifs url. So: only our own media, and `crossOrigin` ONLY there.
 *   Setting it on a foreign url turns a tainting load into a failing one, which is the trade
 *   tools/checks/cache-headers.mts already pins for the player.
 * - SAFE MODE blurs only the paint, in CSS; the pixels a canvas holds stay clear — so a
 *   downloaded card would carry unblurred frames while the on-screen preview is blurred.
 *   Skip imagery entirely.
 * - A poster can simply not load (no age cookie, so the media route 403s; an evicted key; a
 *   dead network). Time it out rather than hanging the render.
 */
export async function loadPosters(
  wanted: Array<{ id: string; posterUrl?: string }>,
): Promise<Map<string, HTMLImageElement>> {
  const out = new Map<string, HTMLImageElement>();
  if (isSafeMode()) return out;
  const usable = wanted.filter((w) => !!w.posterUrl && isOwnMediaUrl(w.posterUrl));
  await Promise.all(
    usable.map(
      (w) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          let done = false;
          const finish = (ok: boolean) => {
            if (done) return;
            done = true;
            if (ok) out.set(w.id, img);
            resolve();
          };
          const t = setTimeout(() => finish(false), POSTER_TIMEOUT_MS);
          img.crossOrigin = "anonymous";
          img.onload = () => {
            clearTimeout(t);
            finish(true);
          };
          img.onerror = () => {
            clearTimeout(t);
            finish(false);
          };
          img.src = w.posterUrl!;
        }),
    ),
  );
  return out;
}
